import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import Login from '../Login';
import IconMask from '../IconMask';
import { useTheme } from '@mui/system';
import { useSelector, useDispatch } from 'react-redux';

// ✅ REST y SOCKET separados
import { app, socketApp, socket } from '../../services';

const height = 60;

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    paddingBottom: height,
    [theme.breakpoints.up('sm')]: {
      paddingTop: height,
      paddingBottom: 0,
    },
  },
  appBar: {
    position: 'fixed',
    bottom: 0,
    height: height,
    width: '100%',
    borderTop: '1px solid rgba(200,200,200,0.2)',
    borderBottom: '1px solid rgba(200,200,200,0.2)',
    backgroundColor: 'inherit',
    boxShadow: 'none',
    [theme.breakpoints.up('sm')]: {
      bottom: 'auto',
      top: 0,
      boxShadow: '0px 0px 10px rgba(50,50,50,0.9)',
    },
  },
}));

function HeaderAppBar(props) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();

  const token = useSelector((state) => state.session.token);

  const [path, setPath] = useState(history.location.pathname);
  const [showLogin, setShowLogin] = useState(!token);

  const lastTokenRef = useRef(null);
  const reauthInFlightRef = useRef(false);

  const routes = useMemo(
    () => [
      { name: 'Conexiones', path: '/devices', icon: 'assets/icons/radar.svg' },
      { name: 'Inicio', path: '/', icon: 'assets/icons/iteration.svg' },
      { name: 'Video', path: '/video-call', icon: 'assets/icons/engineer.svg' },
      { name: 'Config', path: '/config', icon: 'assets/icons/settings.svg' },
    ],
    []
  );

  // -----------------------------
  // Listener navegación
  // -----------------------------
  useEffect(() => {
    const unlisten = history.listen((location) => {
      setPath(location.pathname);
      if (!token && location.pathname !== '/') setShowLogin(true);
    });
    return () => unlisten();
  }, [history, token]);

  // -----------------------------
  // Helpers
  // -----------------------------
  const setTokenAndReauth = async (feathersApp, jwt) => {
    if (!feathersApp?.authentication?.setAccessToken) return false;

    feathersApp.authentication.setAccessToken(jwt);

    try {
      // reAuthenticate usa lo guardado en auth client
      await feathersApp.reAuthenticate();
      return true;
    } catch (e) {
      return false;
    }
  };

  const ensureSocketConnected = () => {
    if (!socket) return;
    if (!socket.connected) {
      try {
        socket.connect(); // autoConnect=false
      } catch (e) {
        console.log('[socket] connect() failed', e);
      }
    }
  };

  // -----------------------------
  // 1) ReAuth inicial REST (si hay token)
  // -----------------------------
  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!token) {
        if (mounted) setShowLogin(true);
        return;
      }

      try {
        await setTokenAndReauth(app, token);
        if (!mounted) return;
        setShowLogin(false);
      } catch (_) {
        if (!mounted) return;
        setShowLogin(true);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // solo una vez

  // -----------------------------
  // 2) Watch token: login/logout
  // -----------------------------
  useEffect(() => {
    const prev = lastTokenRef.current;
    const next = token || null;
    lastTokenRef.current = next;

    if (prev === next) return;

    // Logout
    if (!next) {
      setShowLogin(true);

      try { app.logout(); } catch (_) { }
      try { socketApp.logout(); } catch (_) { }

      if (socket?.connected) {
        try { socket.disconnect(); } catch (_) { }
      }

      dispatch({ type: 'UNSET_USER', payload: null });
      return;
    }

    // Login / Token refresh
    if (reauthInFlightRef.current) return;
    reauthInFlightRef.current = true;

    (async () => {
      setShowLogin(false);

      // REST
      await setTokenAndReauth(app, next);

      // SOCKET
      ensureSocketConnected();

      // si ya conectó, autentica ya
      if (socket?.connected) {
        // opción A (recomendada): authenticate con jwt
        try {
          socketApp.authentication?.setAccessToken?.(next);
          await socketApp.authenticate({ strategy: 'jwt', accessToken: next });
        } catch (e) {
          console.log('[socket] authenticate(jwt) failed', e);
        }
      }
    })().finally(() => {
      reauthInFlightRef.current = false;
    });
  }, [token, dispatch]);

  // -----------------------------
  // 3) Eventos socket: al reconectar → reauth socket
  // -----------------------------
  useEffect(() => {
    if (!socket) return;

    const onConnect = async () => {
      console.log("✅ socket connected", socket.id);
      const jwt = lastTokenRef.current;
      if (!jwt) return;

      try {
        socketApp.authentication?.setAccessToken?.(jwt);
        // reAuth o authenticate(jwt); aquí uso reAuth para que sea “suave”
        await socketApp.reAuthenticate();
        // console.log('[socket] reAuthenticated after connect');
      } catch (e) {
        console.log('[socket] reAuth after connect failed', e);
      }
    };

    const onDisconnect = (reason) => {
      console.log('[socket] disconnected', reason);
      // No forzar login por caída de red
    };

    const onConnectError = (err) => {
      console.log('[socket] connect_error', err?.message || err);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };
  }, []);

  return (
    <React.Fragment>
      <CssBaseline />

      <Grid container className={classes.appBar}>
        {routes.map((route) => (
          <Grid key={route.path} item xs>
            <Button
              onClick={() => history.push(route.path)}
              fullWidth
              size="large"
              style={{ height: '100%' }}
            >
              <IconMask
                color={route.path === path ? theme.palette.primary.main : 'gray'}
                url={route.icon}
              />
            </Button>
          </Grid>
        ))}
      </Grid>

      <Box className={classes.container}>{props.children}</Box>

      <Toolbar />

      <Login open={showLogin} setShowLogin={setShowLogin} />
    </React.Fragment>
  );
}

HeaderAppBar.propTypes = {
  children: PropTypes.element.isRequired,
};

export default HeaderAppBar;
